/* ============================================================
   Promotional popup — popup.md
   Trigger, frequency suppression and dismissal for the <dialog>
   rendered by sections/popup.liquid.

   Event-delegated and idempotent, so it survives theme-editor
   section re-renders without double-binding listeners.
   ============================================================ */

if (!window.__popupInit) {
  window.__popupInit = true;

  const STORAGE_KEY = 'popup-dismissed';
  const html = document.documentElement;
  const designMode = typeof Shopify !== 'undefined' && Shopify.designMode;

  // Carries the suppression for the rest of this page load when
  // localStorage is unavailable (private browsing, blocked storage).
  let memoryDismissed = false;
  let lastFocused = null;

  const readState = () => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (typeof parsed.t !== 'number' || typeof parsed.k !== 'string') return null;
      return parsed;
    } catch (e) {
      return null;
    }
  };

  const writeState = (resetKey) => {
    memoryDismissed = true;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ t: Date.now(), k: resetKey }));
    } catch (e) {
      // Nothing to do — memoryDismissed already holds the suppression.
    }
  };

  // The stored value holds the dismissal timestamp, never a computed
  // expiry, so editing the frequency applies to existing visitors at once.
  const isSuppressed = (popup) => {
    if (memoryDismissed) return true;
    const state = readState();
    if (!state) return false;
    if (state.k !== popup.dataset.resetKey) return false;
    const hours = parseInt(popup.dataset.frequencyHours, 10) || 2;
    return Date.now() - state.t < hours * 3600000;
  };

  const lock = () => {
    // Measure before hiding overflow, or the scrollbar has already gone
    // and the compensation is always 0.
    const shift = window.innerWidth - html.clientWidth;
    html.style.overflow = 'hidden';
    if (shift > 0) html.style.paddingRight = shift + 'px';
  };

  const unlock = () => {
    html.style.overflow = '';
    html.style.paddingRight = '';
  };

  const openPopup = (popup) => {
    if (popup.hasAttribute('open')) return;

    const active = document.activeElement;
    const restorable = active && active !== document.body && active !== html && !popup.contains(active);
    lastFocused = restorable ? active : null;

    if (typeof popup.showModal === 'function') popup.showModal();
    else popup.setAttribute('open', '');

    lock();
  };

  const closePopup = (popup) => {
    if (typeof popup.close === 'function') {
      popup.close();
    } else {
      popup.removeAttribute('open');
      popup.dispatchEvent(new Event('close'));
    }
  };

  // Never stack over another dialog — most often the age gate on a first
  // visit. Wait for it, then open.
  const openWhenFree = (popup) => {
    if (popup.hasAttribute('open')) return;
    const blocker = document.querySelector('dialog[open]');
    if (blocker && blocker !== popup) {
      blocker.addEventListener('close', () => openWhenFree(popup), { once: true });
      return;
    }
    openPopup(popup);
  };

  const armTrigger = (popup) => {
    // The image sits inside a closed <dialog>, so loading="lazy" holds its
    // fetch until the dialog opens — which made it the LCP element, paid for
    // entirely at open time. Start it now instead, at low priority so it
    // never competes with the hero. This runs only once the popup is known to
    // open, so a suppressed popup still costs nothing.
    const image = popup.querySelector('.popup__image');
    if (image && image.loading === 'lazy') {
      image.fetchPriority = 'low';
      image.loading = 'eager';
    }

    const delay = parseInt(popup.dataset.delay, 10);
    const delayMs = (isNaN(delay) ? 4 : delay) * 1000;
    const armDelay = () => window.setTimeout(() => openWhenFree(popup), delayMs);

    if (popup.dataset.trigger === 'scroll') {
      // A page shorter than the viewport can never reach the depth.
      if (html.scrollHeight <= window.innerHeight) {
        armDelay();
        return;
      }

      const depth = parseInt(popup.dataset.scrollDepth, 10) || 40;
      const onScroll = () => {
        const max = html.scrollHeight - window.innerHeight;
        if (max <= 0) return;
        if ((window.scrollY / max) * 100 < depth) return;
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
        openWhenFree(popup);
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      onScroll();
      return;
    }

    if (popup.dataset.trigger === 'exit') {
      // Touch devices produce no meaningful mouseout, so exit intent would
      // never fire there.
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        armDelay();
        return;
      }

      const onMouseOut = (event) => {
        if (event.relatedTarget !== null || event.clientY > 0) return;
        document.removeEventListener('mouseout', onMouseOut);
        openWhenFree(popup);
      };

      document.addEventListener('mouseout', onMouseOut);
      return;
    }

    armDelay();
  };

  document.addEventListener('click', (event) => {
    const closer = event.target.closest('[data-popup-close], [data-popup-dismiss]');
    if (closer) {
      const popup = closer.closest('[data-popup]');
      if (popup) closePopup(popup);
      return;
    }

    // A ::backdrop click reports the dialog itself as the target, so the
    // pointer position against the panel box is the only "outside" signal.
    if (event.target.matches('[data-popup]')) {
      const popup = event.target;
      const panel = popup.querySelector('.popup__panel');
      if (!panel) return;
      const rect = panel.getBoundingClientRect();
      const outside =
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom;
      if (outside) closePopup(popup);
    }
  });

  // Every dismissal route — the X, the backdrop, Escape, the secondary link
  // — ends here, so the suppression write happens once, in one place.
  document.addEventListener(
    'close',
    (event) => {
      if (!(event.target instanceof Element) || !event.target.matches('[data-popup]')) return;
      const popup = event.target;

      unlock();

      if (!designMode) writeState(popup.dataset.resetKey);

      if (lastFocused && lastFocused.isConnected) {
        lastFocused.focus();
      } else {
        // Opened by a timer, so there is nothing for the UA to restore.
        // #MainContent already carries tabindex="-1" and .focus-none.
        const main = document.getElementById('MainContent');
        if (main) main.focus();
      }

      lastFocused = null;
    },
    true
  );

  if (designMode) {
    // The merchant has to be able to see the section they are editing.
    // Editor opens ignore the trigger and the suppression window.
    document.addEventListener('shopify:section:select', (event) => {
      const popup = event.target.querySelector('[data-popup]');
      if (popup) openPopup(popup);
    });

    document.addEventListener('shopify:section:deselect', (event) => {
      const popup = event.target.querySelector('[data-popup]');
      if (popup) closePopup(popup);
    });
  } else {
    const popup = document.querySelector('[data-popup]');
    if (popup && !isSuppressed(popup)) armTrigger(popup);
  }
}
